import React, {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {Box, InputAdornment, Typography} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {t} from 'i18next';
import './DataImportsFilter.scss';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import ArrSingleSelectDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ISelectItem} from '../../../../shared/types';
import {IngestionDatasetResponse} from '../../../../api/types/ingestionApi.type';
import {RootState} from '../../../../redux/store';
const EXCLUDED_SORT_FIELDS = {
  CUSTODIAN_NAME: 'DatasetCustodianName',
} as const;

type DataImportsFilterProps = {
  data?: IngestionDatasetResponse;
  totalImports: number;
  onSortChange?: (sortType: string) => void;
  onDatasetTypeChange?: (datasetType: string) => void;
  onSearchTextChange?: (searchText: string) => void;
  /**
   * If true, disables all controls in the filter panel.
   * Useful for scenarios where data is being loaded or an error has occurred.
   */
  disableControls?: boolean;
};

const DataImportsFilter: React.FC<DataImportsFilterProps> = ({
  data,
  totalImports,
  onSortChange,
  onDatasetTypeChange,
  onSearchTextChange,
  disableControls,
}) => {
  const [selectedDataSortType, setSelectedDataSortType] =
    useState<ISelectItem | null>(null);
  const [selectedDatasetType, setSelectedDatasetType] =
    useState<ISelectItem | null>(null);
  const [searchInput, setSearchInput] = useState<string>('');

  const roleId = useSelector(
    (state: RootState) => state.authenticatedUser.user?.roleId
  );

  const datasetTypeList: ISelectItem[] = useMemo(() => {
    return (data?.datasetTypeList ?? [])
      .filter((item) => item.value !== '2')
      .map((item) => ({
        key: item.value,
        value: item.value,
        label:
          item.value === '-1'
            ? t('dataImports.filters.allDataImports')
            : item.text,
      }))
      .sort((a, b) => a.value!.localeCompare(b.value!));
  }, [data?.datasetTypeList]);

  const sortingOptions: ISelectItem[] = useMemo(() => {
    return (data?.sortFields ?? [])
      .filter((item) => item.value !== EXCLUDED_SORT_FIELDS.CUSTODIAN_NAME)
      .map((item) => ({
        key: item.value,
        value: item.value,
        label: item.text,
        isDefault: item.isSelected,
      }));
  }, [data?.sortFields]);

  useEffect(() => {
    // Set default selected dataset type
    const defaultDatasetType = datasetTypeList.find(
      (option) => option.value === '-1'
    );
    if (defaultDatasetType && !selectedDatasetType) {
      setSelectedDatasetType(defaultDatasetType);
    }
  }, [datasetTypeList, selectedDatasetType]);

  useEffect(() => {
    // Set default selected sort type
    const defaultSortType = sortingOptions.find(
      (option) => option.key === 'LastModifiedOn'
    );

    if (defaultSortType && !selectedDataSortType) {
      setSelectedDataSortType(defaultSortType);
    }
  }, [sortingOptions, selectedDataSortType]);

  useEffect(() => {
    // Notify parent component when sort type changes
    if (onSortChange && selectedDataSortType?.value) {
      onSortChange(selectedDataSortType.value.toString());
    }
  }, [selectedDataSortType, onSortChange]);

  useEffect(() => {
    // Notify parent component when dataset type changes
    if (onDatasetTypeChange && selectedDatasetType?.value) {
      onDatasetTypeChange(selectedDatasetType.value.toString());
    }
  }, [selectedDatasetType, onDatasetTypeChange]);

  return (
    <Box className="data-imports-filter-panel">
      <Box className="panel-header">
        <Typography
          className="typo-text subhead2_size title-label"
          data-testid={`data-imports-filter-heading`}
        >
          {t('dataImports.filters.heading')}
          <span data-testid={`data-imports-filter-total-imports`}>
            {roleId === 2 ? `(0)` : `(${totalImports.toLocaleString()})`}
          </span>
        </Typography>
        <Typography
          className="typo-text body2_size sub-label"
          data-testid={`data-imports-filter-sub-total-count`}
        >
          {t('dataImports.filters.sub-heading', {
            totalCount: (data?.publishedDocCount ?? 0).toLocaleString(),
          })}
        </Typography>
      </Box>
      <Box className="filter-panel-controls">
        <ArrTextField
          id="search-bar"
          className={`search-textbox ${roleId === 2 && ' search-textbox-disabled'}`}
          sizeVariant="large"
          placeholder={t('dataImports.filters.textInputPlaceholder')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
              onSearchTextChange?.(searchInput);
            }
          }}
          state={roleId === 2 ? 'inactive' : 'enabled'}
          type="search"
          dataTestId="dif-search-text"
          onClear={() => {
            setSearchInput('');
            onSearchTextChange?.('');
          }}
        />
        {roleId !== 2 && (
          <div
            className="data-imports-item-flex"
            data-testid={`data-imports-filter-dropdown-wrapper-selected-data-set`}
          >
            <ArrSingleSelectDropdown
              dropdownLabel={t('dataImports.filters.show')}
              dropdownItems={datasetTypeList ?? []}
              selectedValues={selectedDatasetType as any}
              onSelectionChange={setSelectedDatasetType}
              sizeVariant="large"
              dropdownItemKey="key"
              dropdownItemDisplay="label"
              disabled={disableControls}
              dataTestId="asd-selectedDatasetType"
            />
          </div>
        )}
        <div
          className={`data-imports-item-flex ${roleId === 2 && ' data-imports-item-flex-disabled'}`}
          data-testid={`data-imports-filter-div-wrapper-data-sort-type`}
        >
          <ArrSingleSelectDropdown
            dropdownLabel={t('dataImports.filters.sortBy')}
            dropdownItems={sortingOptions ?? []}
            selectedValues={selectedDataSortType as any}
            onSelectionChange={setSelectedDataSortType}
            sizeVariant="large"
            dropdownItemKey="key"
            dropdownItemDisplay="label"
            disabled={roleId === 2 ? true : disableControls}
            dataTestId="asd-setSelectedDataSortType"
          />
        </div>
      </Box>
    </Box>
  );
};

export default DataImportsFilter;